import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Puzzle, Plus, Trash2, PlayCircle, Loader2, Webhook, ListTree, ChevronDown, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];
const ENTITIES = [
  { key: "gout_projects", label: "GOÛT" }, { key: "tagma_campaigns", label: "TAGMA" },
  { key: "crm_leads", label: "CRM" }, { key: "vetta_products", label: "VETTA" },
  { key: "aura_products", label: "AURA" }, { key: "ops_projects", label: "Operaciones" },
];

function WebhooksTab({ t }) {
  const [hooks, setHooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", event: "*", url: "", secret: "" });
  const [creating, setCreating] = useState(false);
  const [openDel, setOpenDel] = useState(null);
  const [deliveries, setDeliveries] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try { setHooks((await api.get("/extensions")).data); } catch { /* */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim() || !form.url.trim()) return;
    setCreating(true);
    try { await api.post("/extensions", form); toast.success(t("os.ext.created")); setForm({ name: "", event: "*", url: "", secret: "" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setCreating(false); }
  };
  const toggle = async (h) => { await api.patch(`/extensions/${h.id}`, { enabled: !h.enabled }); load(); };
  const del = async (id) => { await api.delete(`/extensions/${id}`); toast.success(t("os.ext.deleted")); load(); };
  const test = async (id) => {
    try { const r = await api.post(`/extensions/${id}/test`); toast.success(`${t("os.ext.tested")} · ${r.data.delivery?.code || "-"}`); loadDeliveries(id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const loadDeliveries = async (id) => {
    const r = await api.get(`/extensions/${id}/deliveries`);
    setDeliveries((p) => ({ ...p, [id]: r.data }));
    setOpenDel((o) => (o === id ? o : id));
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="ext-webhooks">
      <div className="lg:col-span-1">
        <div className="card-surface p-5 space-y-3">
          <h3 className="font-medium flex items-center gap-2"><Plus className="w-4 h-4 text-violet" /> {t("os.ext.newWebhook")}</h3>
          <input className="field" placeholder={t("os.ext.name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ext-wh-name" />
          <input className="field" placeholder={t("os.ext.url")} value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="ext-wh-url" />
          <input className="field" placeholder={t("os.ext.event")} value={form.event} onChange={(e) => setForm({ ...form, event: e.target.value })} data-testid="ext-wh-event" />
          <p className="text-[11px] text-navy/40">{t("os.ext.eventHint")}</p>
          <input className="field" placeholder={t("os.ext.secret")} value={form.secret} onChange={(e) => setForm({ ...form, secret: e.target.value })} data-testid="ext-wh-secret" />
          <button onClick={create} disabled={creating} className="btn btn-primary w-full" data-testid="ext-wh-create">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.ext.create")}
          </button>
        </div>
      </div>
      <div className="lg:col-span-2">
        {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
          : hooks.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm" data-testid="ext-no-webhooks">{t("os.ext.noWebhooks")}</div>
          : (
            <div className="space-y-3">
              {hooks.map((h) => (
                <div key={h.id} className="card-surface p-4" data-testid={`ext-wh-${h.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium flex items-center gap-2"><Webhook className="w-4 h-4 text-violet" /> {h.name}</div>
                      <div className="text-xs text-navy/50 mt-1 truncate font-mono">{h.url}</div>
                      <span className="badge badge-muted mt-2">{h.event}</span>
                    </div>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer shrink-0">
                      <input type="checkbox" checked={h.enabled} onChange={() => toggle(h)} className="accent-violet" data-testid={`ext-wh-toggle-${h.id}`} /> {t("os.ext.enabled")}
                    </label>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <button onClick={() => test(h.id)} className="btn btn-outline text-xs" data-testid={`ext-wh-test-${h.id}`}><PlayCircle className="w-3.5 h-3.5" /> {t("os.ext.test")}</button>
                    <button onClick={() => (openDel === h.id ? setOpenDel(null) : loadDeliveries(h.id))} className="btn btn-ghost text-xs" data-testid={`ext-wh-deliveries-${h.id}`}>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openDel === h.id ? "rotate-180" : ""}`} /> {t("os.ext.deliveries")}
                    </button>
                    <button onClick={() => del(h.id)} className="text-navy/30 hover:text-destructive ml-auto" data-testid={`ext-wh-del-${h.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {openDel === h.id && (
                    <div className="mt-3 border-t border-navy/10 pt-2 space-y-1">
                      {(deliveries[h.id] || []).length === 0 ? <div className="text-[11px] text-navy/40">{t("os.ext.noDeliveries")}</div>
                        : deliveries[h.id].map((d) => (
                          <div key={d.id} className="flex items-center gap-2 text-[11px]">
                            {d.status === "ok" ? <Check className="w-3 h-3 text-emerald-600" /> : <AlertTriangle className="w-3 h-3 text-destructive" />}
                            <span className="font-mono text-navy/40">{d.code || "—"}</span>
                            <span className="text-navy/60">{d.event}</span>
                            <span className="text-navy/30 ml-auto">{(d.at || "").slice(11, 19)}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

function FieldsTab({ t }) {
  const [defs, setDefs] = useState([]);
  const [form, setForm] = useState({ entity_type: "gout_projects", key: "", label: "", field_type: "text", options: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => { setDefs((await api.get("/extensions/custom-fields")).data); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.key.trim() || !form.label.trim()) return;
    setSaving(true);
    try {
      await api.post("/extensions/custom-fields", { ...form, options: form.options.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success(t("os.ext.fieldCreated")); setForm({ ...form, key: "", label: "", options: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/extensions/custom-fields/${id}`); toast.success(t("os.ext.deleted")); load(); };

  const types = [["text", t("os.ext.typeText")], ["number", t("os.ext.typeNumber")], ["date", t("os.ext.typeDate")], ["select", t("os.ext.typeSelect")], ["boolean", t("os.ext.typeBoolean")]];

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="ext-fields">
      <div className="lg:col-span-1">
        <div className="card-surface p-5 space-y-3">
          <h3 className="font-medium flex items-center gap-2"><Plus className="w-4 h-4 text-violet" /> {t("os.ext.addField")}</h3>
          <select className="field" value={form.entity_type} onChange={(e) => setForm({ ...form, entity_type: e.target.value })} data-testid="ext-fd-entity">
            {ENTITIES.map((en) => <option key={en.key} value={en.key}>{en.label}</option>)}
          </select>
          <input className="field" placeholder={t("os.ext.key")} value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} data-testid="ext-fd-key" />
          <input className="field" placeholder={t("os.ext.label")} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="ext-fd-label" />
          <select className="field" value={form.field_type} onChange={(e) => setForm({ ...form, field_type: e.target.value })} data-testid="ext-fd-type">
            {types.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {form.field_type === "select" && <input className="field" placeholder={t("os.ext.options")} value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })} data-testid="ext-fd-options" />}
          <button onClick={create} disabled={saving} className="btn btn-primary w-full" data-testid="ext-fd-create">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t("os.ext.addField")}
          </button>
          <p className="text-[11px] text-navy/40">{t("os.ext.fieldsHint")}</p>
        </div>
      </div>
      <div className="lg:col-span-2">
        {defs.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm" data-testid="ext-no-fields">{t("os.ext.noFields")}</div>
          : (
            <div className="space-y-2">
              {defs.map((f) => (
                <div key={f.id} className="card-surface p-4 flex items-center justify-between" data-testid={`ext-fd-${f.id}`}>
                  <div>
                    <div className="font-medium">{f.label} <span className="text-navy/40 font-mono text-xs">· {f.key}</span></div>
                    <div className="text-xs text-navy/50 mt-0.5">
                      <span className="badge badge-muted">{ENTITIES.find((e) => e.key === f.entity_type)?.label || f.entity_type}</span>
                      <span className="badge badge-violet ml-1">{f.field_type}</span>
                    </div>
                  </div>
                  <button onClick={() => del(f.id)} className="text-navy/30 hover:text-destructive" data-testid={`ext-fd-del-${f.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}

export default function Extensiones() {
  const { t } = useI18n();
  const [tab, setTab] = useState("webhooks");

  return (
    <OSLayout title="Extensiones">
      <div className="space-y-8" data-testid="extensiones-page">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Puzzle className="w-3.5 h-3.5" /> {t("os.ext.overline")}</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">{t("os.ext.title")}</h2>
          <p className="mt-2 text-navy/50 max-w-2xl">{t("os.ext.subtitle")}</p>
        </motion.div>

        <div className="flex gap-2">
          <button onClick={() => setTab("webhooks")} data-testid="ext-tab-webhooks" className={`btn text-sm ${tab === "webhooks" ? "btn-primary" : "btn-ghost"}`}><Webhook className="w-4 h-4" /> {t("os.ext.tabWebhooks")}</button>
          <button onClick={() => setTab("fields")} data-testid="ext-tab-fields" className={`btn text-sm ${tab === "fields" ? "btn-primary" : "btn-ghost"}`}><ListTree className="w-4 h-4" /> {t("os.ext.tabFields")}</button>
        </div>

        {tab === "webhooks" ? <WebhooksTab t={t} /> : <FieldsTab t={t} />}
      </div>
    </OSLayout>
  );
}
